/**
 * App shell: routing, the two persistent banners, and outbox flushing.
 *
 * The impersonation banner is the important one. It renders whenever the server
 * says an ABG admin is acting as this captain, it is sticky at the top of every
 * screen, and it carries the one-click exit. It is not dismissible and its
 * visibility is not a client decision -- see lib/auth.tsx.
 */

import { useCallback, useEffect, useState } from 'react'
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import { Spinner } from '@abg/ui'
import { BottomNav } from './components/BottomNav'
import { api, ApiError } from './lib/api'
import { useAuth } from './lib/auth'
import { flushOutbox, onOutboxChange, listOutbox, type OutboxEntry } from './lib/offline'
import { ApplicationDetailPage } from './pages/ApplicationDetail'
import { ApplicationsPage } from './pages/Applications'
import { ApplyPage } from './pages/Apply'
import { FriendsPage } from './pages/Friends'
import { InvitePage } from './pages/Invite'
import { LoginPage } from './pages/Login'
import { MapPage } from './pages/MapPage'
import { NotificationsPage } from './pages/Notifications'
import { OnboardingPage } from './pages/Onboarding'
import { SettingsPage } from './pages/Settings'

export function App() {
  const { user, loading } = useAuth()
  const navigate = useNavigate()
  const [online, setOnline] = useState(navigator.onLine)
  const [pending, setPending] = useState(0)
  const [flushNote, setFlushNote] = useState<string | null>(null)

  // -------------------------------------------------------------- outbox
  const flush = useCallback(async () => {
    if (!navigator.onLine || !user) return
    const result = await flushOutbox(
      (entry: OutboxEntry) =>
        api.post(entry.path, entry.body).then(() => undefined),
      (entry, message) => setFlushNote(`${entry.label} was rejected: ${message}`),
    )
    setPending(result.remaining)
    if (result.sent > 0) {
      setFlushNote(`${result.sent} queued ${result.sent === 1 ? 'item' : 'items'} sent.`)
    }
  }, [user])

  useEffect(() => {
    const goOnline = () => {
      setOnline(true)
      void flush()
    }
    const goOffline = () => setOnline(false)
    // Three triggers, because no single one is reliable: 'online' misfires on
    // captive portals, visibility covers the app being reopened, and the
    // service worker's Background Sync is absent on iOS.
    window.addEventListener('online', goOnline)
    window.addEventListener('offline', goOffline)
    const onVisible = () => {
      if (document.visibilityState === 'visible') void flush()
    }
    document.addEventListener('visibilitychange', onVisible)

    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === 'flush-outbox') void flush()
      if (event.data?.type === 'navigate' && typeof event.data.url === 'string') {
        navigate(event.data.url)
      }
    }
    navigator.serviceWorker?.addEventListener('message', onMessage)

    return () => {
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
      document.removeEventListener('visibilitychange', onVisible)
      navigator.serviceWorker?.removeEventListener('message', onMessage)
    }
  }, [flush, navigate])

  useEffect(() => {
    const refresh = () => void listOutbox().then((rows) => setPending(rows.length))
    refresh()
    return onOutboxChange(refresh)
  }, [])

  useEffect(() => {
    if (user) void flush()
  }, [user, flush])

  useEffect(() => {
    if (!flushNote) return
    const timer = window.setTimeout(() => setFlushNote(null), 6000)
    return () => window.clearTimeout(timer)
  }, [flushNote])

  // ------------------------------------------------------------ rendering
  if (loading) {
    return (
      <div className="app-splash">
        <Spinner label="Signing in…" />
      </div>
    )
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/invite/:token" element={<InvitePage />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    )
  }

  return (
    <div className="app">
      {user.actor_id && <ImpersonationBanner />}
      {!online && (
        <div className="offline-bar" role="status">
          Offline — {pending > 0 ? `${pending} waiting to send` : 'showing saved data'}
        </div>
      )}
      {online && pending > 0 && (
        <div className="offline-bar" role="status">
          {pending} {pending === 1 ? 'item' : 'items'} waiting to send
        </div>
      )}
      {flushNote && (
        <div className="offline-bar" role="status" style={{ background: 'var(--sea-green-wash)', color: 'var(--sea-green)', borderColor: 'var(--sea-green)' }}>
          {flushNote}
        </div>
      )}

      <main className="app-main">
        <Routes>
          <Route path="/" element={<MapPage />} />
          <Route path="/apply" element={<ApplyPage />} />
          <Route path="/applications" element={<ApplicationsPage />} />
          <Route path="/applications/:id" element={<ApplicationDetailPage />} />
          <Route path="/friends" element={<FriendsPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/invite/:token" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <BottomNav />
    </div>
  )
}

/**
 * "ABG is acting as you."
 *
 * Shown to the captain's session only when an admin is genuinely inside it. The
 * exit button is here as well as in the admin app so that whoever is looking at
 * the screen can end it -- including the captain, if they are watching over
 * someone's shoulder on a video call.
 */
function ImpersonationBanner() {
  const { user, reload } = useAuth()
  const [leaving, setLeaving] = useState(false)

  const exit = async () => {
    setLeaving(true)
    try {
      await api.post('/api/auth/impersonate/exit')
    } catch (error) {
      if (!(error instanceof ApiError)) throw error
    } finally {
      // Whatever happened, re-read the truth from the server rather than
      // assuming the banner should now be gone.
      await reload()
      setLeaving(false)
    }
  }

  return (
    <div className="impersonation-bar" role="alert">
      <span>
        Superyacht Services ({user?.actor_name}) is acting as{' '}
        {user?.acting_as_name ?? 'you'}
        {user?.vessel_name ? ` · ${user.vessel_name}` : ''}
      </span>
      <button type="button" className="btn btn-sm" onClick={exit} disabled={leaving}>
        {leaving ? 'Exiting…' : 'Exit'}
      </button>
    </div>
  )
}
